import { useState, useEffect, useCallback } from 'react';
import { Col, Row, Divider, Radio, Space, Checkbox, Form } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
    createClient,
    createClientDataStore,
} from '../../redux/slices/clientsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    GeneralDataInputs,
    UrlsDataInputs,
    CompanyDataInputs,
} from './DataInputs';
import MultinationalTabs from '../MultinationalTabs/MultinationalTabs';
import { allThemes, getThemes } from '../../redux/slices/themeSlice';
import CustomSelect from '../CustomSelect/CustomSelect';
import { accountTypesOptions } from '../../shared/data/clientDetailsOptions';
import { defaultWhiteLabelColors } from '../../shared/data/defaultWhiteLabelColors';
import { CriiptoMethods } from '../CriiptoMethods';
import {
    createShallowCopy,
    getClientFormData,
} from '../../utils/helpFunctions';
import {
    dataStoreData,
    logosErrors,
    logosStateData,
    mainStateData,
} from './data/initialStateData';
import CustomMultiselect from '../CustomMultiselect';
import { countryCodesMap } from '../../shared/data/countryCodesMap';

const REGEXP_ACCEPT_IMAGE = /(image\/svg\+xml|image\/png|image\/jpe?g)/i;
const excludedCodes = ['en']

export default function CreateForm() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const themes = useSelector(allThemes);

    const [radioButtonValue, setRadioButtonValue] = useState(null);
    const [selectedIntegration, setSelectedIntegration] =
        useState('noIntegration');

    const [data, setData] = useState(mainStateData);
    const [dataStore, setDataStore] = useState(dataStoreData);
    const [logos, setLogos] = useState(logosStateData);
    const [logoErrors, setLogoErrors] = useState(logosErrors);
    const [errors, setErrors] = useState({});
    const [selectOptionsThemes, setSelectOptionsThemes] = useState([]);
    const selectedCriiptoIntegration = selectedIntegration === 'criipto';

    useEffect(() => {
        dispatch(getThemes());
    }, [dispatch]);

    useEffect(() => {
        if (themes) {
            const selectOptions = themes?.reduce((acc, el) => {
                acc.push({
                    id: el.id,
                    value: el.name.toLowerCase(),
                    label: el.name,
                });

                return acc;
            }, []);
            setSelectOptionsThemes(selectOptions);
            setData((prev) => ({ ...prev, theme_id: selectOptions[0]?.id }));
            setDataStore((prev) => ({
                ...prev,
                theme: selectOptions[0]?.label,
            }));
        }
    }, [themes]);

    const onChange = (e) => {
        const value = e.target.value;
        const fieldId = e.target.id;
        setData({
            ...data,
            [fieldId]: value,
        });
        if (fieldId === 'api_url') {
            setDataStore({ ...dataStore, api_url: value });
        }
    };

    const onChangeMultinational = (e, flag) => {
        const currentInput = e.target.id.slice(0, e.target.id.length - 3);
        const value = e.target.value;
        const newData = {
            ...data,
            countries_localizations: {
                ...data.countries_localizations,
                [flag]: {
                    ...data.countries_localizations[flag],
                    [currentInput]: value,
                },
            },
        };

        setData(newData);
    };
    const onChangeMultinationalDataStore = (e, flag) => {
        const currentInput = e.target.id.slice(0, e.target.id.length - 3);
        const value = e.target.value;
        const newData = {
            ...dataStore,
            countries_localizations: {
                ...dataStore.countries_localizations,
                [flag]: {
                    ...dataStore.countries_localizations[flag],
                    [currentInput]: value,
                },
            },
        };

        setDataStore(newData);
    };
    const onRadioButtonsChange = (e) => {
        setRadioButtonValue(e.target.value);
        setDataStore({
            ...dataStore,
            email_required: e.target.value === 'email' ? true : false,
            ssn_required: e.target.value === 'ssn' ? true : false,
        });
    };

    const onCheckboxChangeDataStore = (e) => {
        const { name, checked } = e.target;
        setErrors({ ...errors, is_login_email_form_enabled: null });
        setDataStore({
            ...dataStore,
            [name]: checked,
        });
    };

    const onCancelButtonClick = (e) => {
        e.preventDefault();
        navigate(-1);
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        const requiredFieldErrors = validateForm();
        setErrors({
            ...errors,
            ...requiredFieldErrors,
        });

        if (Object.keys(requiredFieldErrors).length > 0) {
            return;
        }

        const fields = []; 

        fields.push({
            key_name: 'account_languages',
            value: ['EN', 'SV', 'FI', 'DA', 'NO'],
        });

        fields.push({
            key_name: 'account_countries',
            value: ['SE', 'FI', 'NO', 'DK', 'IS'],
        });

        if (!dataStore.white_label_colors) {
            fields.push({
                key_name: 'white_label_colors',
                value: defaultWhiteLabelColors,
            });
        }

        Object.keys(dataStore).forEach((key) => {
            const value = dataStore[key];
            if (typeof value === 'boolean' || value) {
                fields.push({ key_name: key, value });
            }
        });
        const bodyDataStore = { fields };

        try {
            const response = await dispatch(
                createClient({
                    body: getClientFormData(logos, data),
                }),
            ).unwrap();
            if (Object.keys(bodyDataStore.fields).length !== 0) {
                await dispatch(
                    createClientDataStore({
                        id: response.client_uid,
                        body: bodyDataStore,
                        collectionName: 'clients',
                    }),
                ).unwrap();
            }
            toast.success('New client successfully created');
            navigate('../clients');
        } catch (error) {
            setErrors(error.errors);
            if (error.errors.name || error.errors.client_uid)
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                });
        }
    };

    const onFileSelect = (e, flag) => {
        const loaderName = e.target.name;

        if (e.target.files && e.target.files[0]) {
            if (!REGEXP_ACCEPT_IMAGE.test(e.target.files[0].type)) {
                if (loaderName === 'big-logo') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            bigLogoErrors: {
                                ...prev[flag].bigLogoErrors,
                                isWrongFileType: true,
                            },
                        },
                    }));
                }
                if (loaderName === 'small-logo') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            smallLogoErrors: {
                                ...prev[flag].smallLogoErrors,
                                isWrongFileType: true,
                            },
                        },
                    }));
                }
                if (loaderName === 'favicon') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            faviconErrors: {
                                ...prev[flag].faviconErrors,
                                isWrongFileType: true,
                            },
                        },
                    }));
                }
                if (loaderName === 'email-logo') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            emailLogoErrors: {
                                ...prev[flag].emailLogoErrors,
                                isWrongFileType: true,
                            },
                        },
                    }));
                }
                return;
            }

            if (e.target.files[0].size > 2097152) {
                if (loaderName === 'big-logo') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            bigLogoErrors: {
                                ...prev[flag].bigLogoErrors,
                                isFileOversized: true,
                            },
                        },
                    }));
                }
                if (loaderName === 'small-logo') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            smallLogoErrors: {
                                ...prev[flag].smallLogoErrors,
                                isFileOversized: true,
                            },
                        },
                    }));
                }
                if (loaderName === 'favicon') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            faviconErrors: {
                                ...prev[flag].faviconErrors,
                                isFileOversized: true,
                            },
                        },
                    }));
                }
                if (loaderName === 'email-logo') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            emailLogoErrors: {
                                ...prev[flag].emailLogoErrors,
                                isFileOversized: true,
                            },
                        },
                    }));
                }
                return;
            }

            setLogoErrors((prev) => ({
                ...prev,
                [flag]: {
                    ...prev[flag],
                    ...(loaderName === 'big-logo' && {
                        bigLogoErrors: {
                            isWrongFileType: false,
                            isFileOversized: false,
                        },
                    }),
                    ...(loaderName === 'small-logo' && {
                        smallLogoErrors: {
                            isWrongFileType: false,
                            isFileOversized: false,
                        },
                    }),
                    ...(loaderName === 'favicon' && {
                        faviconErrors: {
                            isWrongFileType: false,
                            isFileOversized: false,
                        },
                    }),
                    ...prev[flag],
                    ...(loaderName === 'email-logo' && {
                        emailLogoErrors: {
                            isWrongFileType: false,
                            isFileOversized: false,
                        },
                    }),
                },
            }));

            const bigLogoUrl = window.URL.createObjectURL(e.target.files[0]);
            const smallLogoUrl = window.URL.createObjectURL(e.target.files[0]);
            const faviconUrl = window.URL.createObjectURL(e.target.files[0]);
            const emailLogoUrl = window.URL.createObjectURL(e.target.files[0]);

            const file = e.target.files[0];

            if (loaderName === 'big-logo') {
                setLogos((prev) => ({
                    ...prev,
                    [flag]: {
                        ...prev[flag],
                        bigLogo: {
                            logoUrl: bigLogoUrl,
                            logoData: file,
                        },
                    },
                }));
            }
            if (loaderName === 'small-logo') {
                setLogos((prev) => ({
                    ...prev,
                    [flag]: {
                        ...prev[flag],
                        smallLogo: {
                            logoUrl: smallLogoUrl,
                            logoData: file,
                        },
                    },
                }));
            }
            if (loaderName === 'favicon') {
                setLogos((prev) => ({
                    ...prev,
                    [flag]: {
                        ...prev[flag],
                        favicon: {
                            logoUrl: faviconUrl,
                            logoData: file,
                        },
                    },
                }));
            }
            if (loaderName === 'email-logo') {
                setLogos((prev) => ({
                    ...prev,
                    [flag]: {
                        ...prev[flag],
                        emailLogo: {
                            logoUrl: emailLogoUrl,
                            logoData: file,
                        },
                    },
                }));
            }
        }
    };

    const onDeleteLogo = (e, flag = 'default') => {
        e.preventDefault();
        const currentButtonName = e.currentTarget.name;

        setLogos((prev) => ({
            ...prev,
            [flag]: {
                ...prev[flag],
                ...(currentButtonName === 'big-logo' && {
                    bigLogo: { logoUrl: '', logoData: null },
                }),
                ...(currentButtonName === 'small-logo' && {
                    smallLogo: { logoUrl: '', logoData: null },
                }),
                ...(currentButtonName === 'favicon' && {
                    favicon: { logoUrl: '', logoData: null },
                }),
                ...(currentButtonName === 'email-logo' && {
                    emailLogo: { logoUrl: '', logoData: null },
                }),
            },
        }));
    };

    const onSelectChange = (option) => {
        setData({ ...data, theme_id: option.id });
        setDataStore({ ...dataStore, theme: option.label });
    };

    const onMultiselectChange = useCallback(
        (options, selectName) => {
            const opts = options.map((el) => el.value);

            setDataStore({
                ...dataStore,
                [selectName]: opts,
            });
        },
        [dataStore],
    );

    const onSelectedIntegration = (e) => {
        const value = e.target.value;

        setSelectedIntegration(value);
        setErrors({
            ...errors,
            criipto_methods: null,
            is_login_email_form_enabled: null,
        });

        if (value === 'bankId') {
            setDataStore({
                ...dataStore,
                is_bank_id_login_enabled: true,
                is_criipto_integration_enabled: false,
            });

            return;
        }

        if (value === 'criipto') {
            setDataStore({
                ...dataStore,
                is_bank_id_login_enabled: false,
                is_criipto_integration_enabled: true,
            });

            return;
        }

        setDataStore({
            ...dataStore,
            is_bank_id_login_enabled: false,
            is_criipto_integration_enabled: false,
        });
    };

    const validateForm = () => {
        const requiredErrors = {};

        if (
            Object.values(dataStore?.criipto_methods || {}).every(
                (methods) => !methods.length,
            ) &&
            selectedCriiptoIntegration
        ) {
            const errorText = 'Please select at least one method.';
            requiredErrors.criipto_methods = [errorText];
            toast.error(errorText);
        }

        if (
            selectedIntegration === 'noIntegration' &&
            !dataStore.is_login_email_form_enabled
        ) {
            const errorText = 'Please select at least one login method.';
            requiredErrors.is_login_email_form_enabled = [errorText];
            toast.error(errorText);
        }

        if (!dataStore.account_types.length) {
            const errorText = 'Please select at least one account type.';
            requiredErrors.account_types = [errorText];
            toast.error(errorText);
        }

        return requiredErrors;
    };

    const handleCriiptoMethods = (methods) => {
        setDataStore({ ...dataStore, criipto_methods: methods });
        setErrors({ ...errors, criipto_methods: null });
    };

    const createSelectOptions = (value, options) =>
        value.reduce((acc, el) => {
            for (const option of options) {
                if (option.id.includes(el)) acc.push(option);
            }
            return acc;
        }, []);

    const onChangeCompanyInfoDataStore = (e, flag) => {
        const currentInput = e.target.id.slice(0, e.target.id.length - 3);
        const value = e.target.value;
        const dataStoreCopy = createShallowCopy(dataStore);

        dataStoreCopy.company_info[flag][currentInput] = value;
        setDataStore(dataStoreCopy);
    };

    const onLogosSizesChange = (e, flag) => {
        const [logoType, parameter] = e.target.id.split('_');
        const value = e.target.value;
        const dataStoreCopy = createShallowCopy(dataStore);

        dataStoreCopy.logos_sizes.web_app[logoType][countryCodesMap[flag]][
            parameter
        ] = value;

        setDataStore(dataStoreCopy);
    };

    return (
        <Row>
            <Col span={10}>
                <form>
                    <GeneralDataInputs
                        onChange={onChange}
                        data={data}
                        errors={errors}
                    />
                    <div className="form__item-wrapper">
                        <p className="input-label">Theme</p>
                        <CustomSelect
                            name="themes"
                            onChange={onSelectChange}
                            options={selectOptionsThemes}
                            placeholder="Select theme"
                            value={data.theme_id}
                            errors={errors}
                        />
                    </div>
                    <div className="form__item-wrapper">
                        <p className="input-label">Account types</p>
                        <CustomMultiselect
                            onChange={(option) =>
                                onMultiselectChange(option, 'account_types')
                            }
                            options={accountTypesOptions}
                            placeholder="Select account types"
                            value={createSelectOptions(
                                dataStore?.account_types,
                                accountTypesOptions,
                            )}
                            hideSelectedOptions={false}
                            closeMenuOnSelect={false}
                            showAllTab={false}
                            errorText={errors?.account_types}
                        />
                    </div>
                    <Divider />
                    <p className="form__input-group-header">COMPANY INFO</p>
                    <MultinationalTabs excludedCodes={excludedCodes}>
                        <CompanyDataInputs
                            onChange={onChangeMultinational}
                            data={dataStore.company_info}
                            dataStore={dataStore}
                            errors={errors}
                            onFileSelect={onFileSelect}
                            logoErrors={logoErrors}
                            onDeleteLogo={onDeleteLogo}
                            logos={logos}
                            onChangeCompanyInfo={onChangeCompanyInfoDataStore}
                            onLogosSizesChange={onLogosSizesChange}
                        />
                    </MultinationalTabs>
                    <Divider />
                    <p className="form__input-group-header">URLS</p>
                    <MultinationalTabs codeType="countries">
                        <UrlsDataInputs
                            onChange={onChangeMultinationalDataStore}
                            data={dataStore.countries_localizations}
                            errors={errors}
                        />
                    </MultinationalTabs>
                    <Divider />
                    <div className="form__item-wrapper--column">
                        <p className="input-label">Required</p>
                        <Radio.Group
                            onChange={onRadioButtonsChange}
                            value={radioButtonValue}
                        >
                            <Space direction="vertical">
                                <Radio value="email">Email</Radio>
                                <Radio value="ssn">SSN</Radio>
                            </Space>
                        </Radio.Group>
                    </div>
                    <div className="form__item-wrapper--column">
                        <p className="input-label">Choose integration</p>
                        <Radio.Group
                            onChange={onSelectedIntegration}
                            value={selectedIntegration}
                        >
                            <Space direction="vertical" className="width-100">
                                <Radio value="criipto">Criipto</Radio>
                                {selectedCriiptoIntegration && (
                                    <CriiptoMethods
                                        handleCriiptoMethods={
                                            handleCriiptoMethods
                                        }
                                        errors={errors}
                                    />
                                )}
                                {/* <Radio value="bankId">BankId</Radio> */}
                                <Radio value="noIntegration">
                                    No integration
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </div>
                    <div className="form__item-wrapper--column">
                        <p className="input-label">
                            Enable email form for login
                        </p>
                        <Form.Item
                            validateStatus={
                                errors?.is_login_email_form_enabled
                                    ? 'error'
                                    : ''
                            }
                            help={
                                errors?.is_login_email_form_enabled
                                    ? errors?.is_login_email_form_enabled
                                    : ''
                            }
                            style={{ margin: 0 }}
                        >
                            <div style={{ width: '50px' }}>
                                <Checkbox
                                    onChange={onCheckboxChangeDataStore}
                                    name="is_login_email_form_enabled"
                                    checked={
                                        dataStore?.is_login_email_form_enabled
                                    }
                                >
                                    Yes
                                </Checkbox>
                            </div>
                        </Form.Item>
                    </div>
                    <div className="form__item-wrapper--column">
                        <p className="input-label">
                            Enable 2factor auth for login
                        </p>
                        <div style={{ width: '50px' }}>
                            <Checkbox
                                onChange={onCheckboxChangeDataStore}
                                name="is_2fa_enabled"
                                checked={dataStore?.is_2fa_enabled}
                            >
                                Yes
                            </Checkbox>
                        </div>
                    </div>
                    <div className="form__item-wrapper--column">
                        <p className="input-label">
                            Send SMS if phone number is not confirmed
                        </p>
                        <div style={{ width: '50px' }}>
                            <Checkbox
                                onChange={onCheckboxChangeDataStore}
                                name="is_send_sms_if_phone_not_confirmed"
                                checked={
                                    dataStore?.is_send_sms_if_phone_not_confirmed
                                }
                            >
                                Yes
                            </Checkbox>
                        </div>
                    </div>
                    <div className="form__item-wrapper--column">
                        <p className="input-label">
                            Show Powered by Defentry logo
                        </p>

                        <div style={{ width: '50px' }}>
                            <Checkbox
                                onChange={onCheckboxChangeDataStore}
                                name="is_show_powered_by_defentry_logo"
                                checked={
                                    dataStore?.is_show_powered_by_defentry_logo
                                }
                            >
                                Yes
                            </Checkbox>
                        </div>
                    </div>
                    <div className="form__footer">
                        <button
                            className="modal__button modal__button-cancel"
                            onClick={onCancelButtonClick}
                        >
                            Cancel
                        </button>
                        <button
                            className="modal__button modal__button-accept"
                            onClick={onSubmit}
                        >
                            Add
                        </button>
                    </div>
                </form>
            </Col>
        </Row>
    );
}
